<template>
  <!-- Desktop: Side menu -->
  <v-menu v-if="!mobile" location="end" :close-on-content-click="false">
    <template #activator="{ props }">
      <v-list-item v-bind="props" slim>
        <template #prepend>
          <v-icon :icon="mdiTranslate" />
        </template>
        <v-list-item-title>
          {{ t('core.common.language') }}
          <span class="text-caption text-medium-emphasis ml-1">
            ( {{ getCurrentLocaleFlag() }}&nbsp; {{ currentLocaleName }})
          </span>
        </v-list-item-title>
        <template #append>
          <v-icon :icon="mdiChevronRight" />
        </template>
      </v-list-item>
    </template>
    <v-card width="320">
      <v-card-text class="pa-0">
        <v-text-field
          v-model="searchQuery"
          :placeholder="t('core.common.search')"
          :prepend-inner-icon="mdiMagnify"
          density="compact"
          variant="solo"
          hide-details
          flat
          single-line
          class="mb-1"
        />
      </v-card-text>
      <v-divider />
      <v-list density="compact" max-height="400" class="py-0">
        <template v-if="filteredLocales.length > 0">
          <v-list-item
            v-for="loc in filteredLocales"
            :key="loc.code"
            :active="localeStore.isCurrentLocale(loc.code)"
            slim
            @click="handleLocaleChange(loc.code)"
          >
            <template #prepend>
              <span class="text-h6 mr-3">{{ loc.flag || '🌐' }}</span>
            </template>
            <v-list-item-title>{{ loc.name }}</v-list-item-title>
            <v-list-item-subtitle>{{ loc.code.toUpperCase() }}</v-list-item-subtitle>
            <template #append>
              <v-icon v-if="localeStore.isCurrentLocale(loc.code)" :icon="mdiCheck" color="primary" />
            </template>
          </v-list-item>
        </template>
        <v-list-item v-else>
          <v-list-item-title class="text-center text-medium-emphasis">
            {{ t('core.common.noResults') }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>

  <!-- Mobile: List item + Bottom sheet -->
  <template v-else>
    <v-list-item slim @click="showSheet = true">
      <template #prepend>
        <v-icon :icon="mdiTranslate" />
      </template>
      <v-list-item-title>
        {{ t('core.common.language') }}
        <span class="text-caption text-medium-emphasis ml-1">
          ( {{ getCurrentLocaleFlag() }}&nbsp; {{ currentLocaleName }})
        </span>
      </v-list-item-title>
      <template #append>
        <v-icon :icon="mdiChevronRight" />
      </template>
    </v-list-item>

    <v-bottom-sheet v-model="showSheet">
      <v-card class="language-sheet">
        <div class="language-sheet__handle" />
        <div class="language-sheet__header">
          <span class="language-sheet__title">{{ t('core.common.language') }}</span>
          <v-btn
            icon
            variant="text"
            size="small"
            :aria-label="t('core.common.close')"
            @click="showSheet = false"
          >
            <v-icon :icon="mdiClose" size="20" />
          </v-btn>
        </div>
        <div class="language-sheet__search">
          <v-text-field
            v-model="searchQuery"
            :placeholder="t('core.common.search')"
            :prepend-inner-icon="mdiMagnify"
            density="compact"
            variant="solo"
            hide-details
            flat
            single-line
          />
        </div>
        <v-divider />
        <v-list density="compact" class="language-sheet__list">
          <template v-if="filteredLocales.length > 0">
            <v-list-item
              v-for="loc in filteredLocales"
              :key="loc.code"
              :active="localeStore.isCurrentLocale(loc.code)"
              slim
              @click="handleLocaleChange(loc.code)"
            >
              <template #prepend>
                <span class="text-h6 mr-3">{{ loc.flag || '🌐' }}</span>
              </template>
              <v-list-item-title>{{ loc.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ loc.code.toUpperCase() }}</v-list-item-subtitle>
              <template #append>
                <v-icon v-if="localeStore.isCurrentLocale(loc.code)" :icon="mdiCheck" color="primary" />
              </template>
            </v-list-item>
          </template>
          <v-list-item v-else>
            <v-list-item-title class="text-center text-medium-emphasis">
              {{ t('core.common.noResults') }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </v-bottom-sheet>
  </template>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { mdiCheck, mdiChevronRight, mdiClose, mdiMagnify, mdiTranslate } from '@mdi/js'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import { useLocaleStore } from '../../stores/useLocaleStore'
import { useSessionStore } from '@/apps/session/stores/session'

const { t, locale } = useI18n()
const { mobile } = useDisplay()
const localeStore = useLocaleStore()
const session = useSessionStore()

const searchQuery = ref('')
const showSheet = ref(false)

const filteredLocales = computed(() => {
  if (!searchQuery.value) return localeStore.availableLocales

  const query = searchQuery.value.toLowerCase()
  return localeStore.availableLocales.filter(
    (loc) =>
      loc.name.toLowerCase().includes(query) || loc.code.toLowerCase().includes(query),
  )
})

const getCurrentLocaleFlag = () => {
  const currentLocale = localeStore.availableLocales.find((l) => l.code === localeStore.currentLocale)
  return currentLocale?.flag || '🌐'
}

const currentLocaleName = computed(() => localeStore.currentLocaleName)

const handleLocaleChange = (code: string) => {
  localeStore.setLocale(code)
  locale.value = code
  searchQuery.value = ''
  showSheet.value = false

  // Persist language preference to user profile
  session.updateLocale(code).catch(() => {
    // Silently fail — localStorage already has the preference
  })
}
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.language-sheet {
  border-radius: $radius-xl $radius-xl 0 0 !important;

  &__handle {
    width: 32px;
    height: 4px;
    margin: $space-sm auto 0;
    border-radius: $radius-full;
    background: rgba(var(--v-theme-on-surface), 0.2);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $space-md $space-lg;
  }

  &__title {
    font-size: $text-lg;
    font-weight: $font-semibold;
    color: rgba(var(--v-theme-on-surface), $opacity-primary);
  }

  &__search {
    padding: 0 $space-lg $space-sm;
  }

  &__list {
    max-height: 50vh;
    overflow-y: auto;
    padding-bottom: env(safe-area-inset-bottom, $space-sm) !important;
  }
}
</style>
